import { prop } from '@bart.sk-eshop/eshop-defaults/lib';
import { resolveProductName, getImagePath } from './product';
import { resolveBaseUrlByLang } from '../configureTrans';
import {
  DEFAULT_LANG,
  resolveArticleSectionSuffixByLang,
  resolveHtmlLangByLang,
  resolveMetaDescriptionSuffixByLang,
} from './brand';

export function isEmptyObj(obj) {
  if (obj) {
    return Object.entries(obj).length === 0 && obj.constructor === Object;
  }
  return true;
}

/**
 * URL query parameters that must trigger a `noindex` robots meta tag.
 *
 * These pages are duplicates/variants (search, filtering, sorting) and should
 * not be indexed by search engines. We still keep `follow` so the link equity
 * flows through to the canonical pages.
 *
 * Pagination (`?offset=`) is deliberately NOT here - paginated listings stay
 * indexable with a self referencing canonical so deep products keep an internal
 * link and do not become orphan pages.
 */
export const NOINDEX_URL_PARAMS = [
  'q',
  'attribs',
  'range_attribs',
  'sort',
  'sortDir',
  'new',
  'saleFilter',
  'sale',
];

/**
 * Returns true when any of the URL query parameters that should cause a
 * `noindex` robots meta tag is present in the given query object.
 *
 * Accepts an object as parsed by react-router (`location.query`).
 */
export function shouldNoIndexByUrlParams(query: any): boolean {
  if (!query || typeof query !== 'object') {
    return false;
  }
  return NOINDEX_URL_PARAMS.some(
    key =>
      Object.prototype.hasOwnProperty.call(query, key) &&
      query[key] !== undefined &&
      query[key] !== null &&
      query[key] !== '',
  );
}

/**
 * Robots meta value used for non-canonical / parameterized listing pages.
 * `follow` is kept so PageRank still flows to canonical destinations.
 */
export const NOINDEX_FOLLOW = 'noindex, follow';

export function isEmptyListing(
  products: any,
  isFetching?: boolean,
  categoryData?: any,
): boolean {
  if (isFetching || !products) {
    return false;
  }

  if (!prop(categoryData, 'category_id', null)) {
    return false;
  }

  const total = prop(products, 'total', null);

  return total === 0;
}

export function getPaginationOffset(query: any): number {
  if (!query || typeof query !== 'object') {
    return 0;
  }
  const offset = parseInt(query.offset, 10);

  return isNaN(offset) || offset < 0 ? 0 : offset;
}

export function buildListingCanonicalUrl(
  pathname: string,
  query: any,
  lang?: string,
): string {
  if (!pathname || pathname === '/') {
    return '';
  }

  const cleanUrl = getLangAbsoluteUrl(pathname, lang);

  if (shouldNoIndexByUrlParams(query)) {
    return cleanUrl;
  }

  const offset = getPaginationOffset(query);

  return offset > 0 ? `${cleanUrl}?offset=${offset}` : cleanUrl;
}

export const META_DESCRIPTION_MAX_LENGTH = 160;

export const NOINDEX_PATH_SEGMENTS = [
  'kosik',
  'moj-ucet',
  'muj-ucet',
  'prihlasenie',
  'prihlaseni',
  'registracia',
  'registrace',
  'zabudnute-heslo',
  'zapomenute-heslo',
  'nove-heslo',
  'dokoncena-objednavka',
  'vyhladavanie',
  'vyhledavani',
  'activation',
  'preview',
  'chatwoot',
  'odstupenie-od-zmluvy',
  'odstoupeni-od-smlouvy',
  'formular-na-odstupenie-od-zmluvy',
  'formular-pro-odstoupeni-od-smlouvy',
];

export function shouldNoIndexByPathname(pathname?: string): boolean {
  if (!pathname) {
    return false;
  }
  const segments = pathname
    .toLowerCase()
    .split('/')
    .filter(Boolean);

  return segments.some(
    segment => NOINDEX_PATH_SEGMENTS.indexOf(segment) !== -1,
  );
}

const HTML_ENTITIES = {
  AElig: 'Æ',
  Aacute: 'Á',
  Acirc: 'Â',
  Agrave: 'À',
  Aring: 'Å',
  Atilde: 'Ã',
  Auml: 'Ä',
  Ccaron: 'Č',
  Ccedil: 'Ç',
  Dagger: '‡',
  Dcaron: 'Ď',
  ETH: 'Ð',
  Eacute: 'É',
  Ecaron: 'Ě',
  Ecirc: 'Ê',
  Egrave: 'È',
  Euml: 'Ë',
  Iacute: 'Í',
  Icirc: 'Î',
  Igrave: 'Ì',
  Iuml: 'Ï',
  Lacute: 'Ĺ',
  Lstrok: 'Ł',
  Ncaron: 'Ň',
  Ntilde: 'Ñ',
  OElig: 'Œ',
  Oacute: 'Ó',
  Ocirc: 'Ô',
  Ograve: 'Ò',
  Oslash: 'Ø',
  Otilde: 'Õ',
  Ouml: 'Ö',
  Racute: 'Ŕ',
  Rcaron: 'Ř',
  Scaron: 'Š',
  THORN: 'Þ',
  Tcaron: 'Ť',
  Uacute: 'Ú',
  Ucirc: 'Û',
  Ugrave: 'Ù',
  Uring: 'Ů',
  Uuml: 'Ü',
  Yacute: 'Ý',
  Zcaron: 'Ž',
  aacute: 'á',
  acirc: 'â',
  acute: '´',
  aelig: 'æ',
  agrave: 'à',
  amp: '&',
  apos: "'",
  aring: 'å',
  atilde: 'ã',
  auml: 'ä',
  bdquo: '„',
  brvbar: '¦',
  bull: '•',
  ccaron: 'č',
  ccedil: 'ç',
  cedil: '¸',
  cent: '¢',
  copy: '©',
  curren: '¤',
  dagger: '†',
  dcaron: 'ď',
  deg: '°',
  divide: '÷',
  eacute: 'é',
  ecaron: 'ě',
  ecirc: 'ê',
  egrave: 'è',
  eth: 'ð',
  euml: 'ë',
  euro: '€',
  frac12: '½',
  frac14: '¼',
  frac34: '¾',
  gt: '>',
  harr: '↔',
  hellip: '…',
  iacute: 'í',
  icirc: 'î',
  iexcl: '¡',
  igrave: 'ì',
  iquest: '¿',
  iuml: 'ï',
  lacute: 'ĺ',
  laquo: '«',
  larr: '←',
  ldquo: '“',
  lsaquo: '‹',
  lsquo: '‘',
  lstrok: 'ł',
  lt: '<',
  macr: '¯',
  mdash: '—',
  micro: 'µ',
  middot: '·',
  minus: '−',
  nbsp: ' ',
  ncaron: 'ň',
  ndash: '–',
  not: '¬',
  ntilde: 'ñ',
  oacute: 'ó',
  ocirc: 'ô',
  oelig: 'œ',
  ograve: 'ò',
  ordf: 'ª',
  ordm: 'º',
  oslash: 'ø',
  otilde: 'õ',
  ouml: 'ö',
  para: '¶',
  permil: '‰',
  plusmn: '±',
  pound: '£',
  quot: '"',
  racute: 'ŕ',
  raquo: '»',
  rarr: '→',
  rcaron: 'ř',
  rdquo: '”',
  reg: '®',
  rsaquo: '›',
  rsquo: '’',
  sbquo: '‚',
  scaron: 'š',
  sect: '§',
  shy: '­',
  sup1: '¹',
  sup2: '²',
  sup3: '³',
  szlig: 'ß',
  tcaron: 'ť',
  thorn: 'þ',
  times: '×',
  trade: '™',
  uacute: 'ú',
  ucirc: 'û',
  ugrave: 'ù',
  uml: '¨',
  uring: 'ů',
  uuml: 'ü',
  yacute: 'ý',
  yen: '¥',
  yuml: 'ÿ',
  zcaron: 'ž',
};

export function normalizeMetaText(input: any): string {
  if (!input || typeof input === 'object') {
    return '';
  }

  return String(input)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&#x([\da-f]+);/gi, (_, code) =>
      String.fromCharCode(parseInt(code, 16)),
    )
    .replace(/&([a-zA-Z][a-zA-Z0-9]*);/g, (match, name) => {
      const entity = HTML_ENTITIES[name];
      return entity === undefined ? match : entity;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

export function truncateMetaText(
  text: string,
  maxLength: number = META_DESCRIPTION_MAX_LENGTH,
): string {
  if (!text || text.length <= maxLength) {
    return text || '';
  }

  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');

  return `${(lastSpace > maxLength * 0.5
    ? cut.slice(0, lastSpace)
    : cut
  ).replace(/[\s,.;:–-]+$/, '')}…`;
}

export const TITLE_MAX_LENGTH = 60;

export function truncateTitleText(
  text: string,
  maxLength: number = TITLE_MAX_LENGTH,
): string {
  if (!text || text.length <= maxLength) {
    return text || '';
  }

  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');

  return (lastSpace > maxLength * 0.5 ? cut.slice(0, lastSpace) : cut).replace(
    /[\s,.;:–\-|(]+$/,
    '',
  );
}

export function buildPageTitle(title: any, defaultTitle: any): string {
  const pageTitle = normalizeMetaText(title);
  const suffix = normalizeMetaText(defaultTitle);

  if (!pageTitle) {
    return suffix;
  }

  if (!suffix || pageTitle === suffix) {
    return truncateTitleText(pageTitle);
  }

  const withSuffix = `${pageTitle} | ${suffix}`;

  if (withSuffix.length <= TITLE_MAX_LENGTH) {
    return withSuffix;
  }

  return truncateTitleText(pageTitle);
}

export function resolveProductBrand(product: any): string {
  const attribs = prop(product, 'attribs', []);
  const brandAttrib = Array.isArray(attribs)
    ? attribs.find(
        attrib => (prop(attrib, 'attrib_name', '') || '').trim() === 'Značka',
      )
    : null;

  const fromAttrib = brandAttrib
    ? normalizeMetaText(
        prop(prop(brandAttrib, 'values', [])[0], 'attrib_value', ''),
      )
    : '';

  if (fromAttrib) {
    return fromAttrib;
  }

  return normalizeMetaText(prop(product, 'brand.brand_name', ''));
}

export const PRODUCT_DESCRIPTION_MAX_LENGTH = 5000;

export function resolveCurrencyByLang(lang?: string): string {
  return lang === 'cz' ? 'CZK' : 'EUR';
}

export function getProductJsonLdImages(photogallery: any): string[] {
  const gallery = Array.isArray(photogallery) ? photogallery : [];

  return gallery
    .filter(item => typeof item === 'string' && item.length > 0)
    .map(item =>
      /^https?:\/\//i.test(item)
        ? item
        : encodeURI(
            `${process.env.REACT_APP_API_BASE_URL}/image?query=${item}`,
          ),
    );
}

export function getProductJsonLdDescription(product: any, info: any): string {
  const candidates = [
    prop(info, 'description'),
    prop(info, 'annotation'),
    prop(product, 'publish.0.product_description'),
    prop(product, 'short_description'),
  ];

  for (const candidate of candidates) {
    const normalized = normalizeMetaText(candidate);
    if (normalized) {
      return truncateMetaText(normalized, PRODUCT_DESCRIPTION_MAX_LENGTH);
    }
  }

  return '';
}

export function getProductJsonLdOffer(
  product: any,
  info: any,
  lang?: string,
  url?: string,
) {
  const rawPrice = prop(product, 'main_good.final_price', null);
  const price = Number(rawPrice);

  if (rawPrice === null || rawPrice === '' || isNaN(price) || price <= 0) {
    return undefined;
  }

  const priceCurrency =
    prop(product, 'main_good.currency_id') ||
    prop(product, 'main_good.currency.id') ||
    resolveCurrencyByLang(lang);

  const isActive = prop(info, 'isactive', true);
  const onStock = Number(prop(info, 'onStock', 0));

  let availability = 'https://schema.org/OutOfStock';
  if (!isActive) {
    availability = 'https://schema.org/Discontinued';
  } else if (onStock > 0) {
    availability = 'https://schema.org/InStock';
  }

  return {
    price: price.toFixed(2),
    priceCurrency,
    availability,
    url,
  };
}

export function buildMetaDescription(
  candidates: any[],
  { name, brand, lang }: { name?: string; brand?: string; lang?: string } = {},
): string {
  for (const candidate of candidates) {
    const normalized = normalizeMetaText(candidate);
    if (normalized) {
      return truncateMetaText(normalized);
    }
  }

  if (!name) {
    return '';
  }

  const productName = normalizeMetaText(name);
  const brandName = normalizeMetaText(brand);
  const intro =
    brandName &&
    productName.toLowerCase().indexOf(brandName.toLowerCase()) === -1
      ? `${productName} – ${brandName}`
      : productName;

  return truncateMetaText(
    `${intro} – ${resolveMetaDescriptionSuffixByLang(lang)}`,
  );
}

const PRODUCT_DESCRIPTION_MIN_WORDS = 6;

function normalizeWord(word: string): string {
  return word
    .toLowerCase()
    .replace(/^[^\wáäčďéěíĺľňóôŕšťúýžà-ÿ]+|[^\wáäčďéěíĺľňóôŕšťúýžà-ÿ]+$/gi, '');
}

export function stripLeadingProductName(
  text: string,
  productName: string,
): string {
  const nameWords = productName.split(' ').filter(Boolean);
  const textWords = text.split(' ').filter(Boolean);

  let common = 0;
  while (
    common < nameWords.length &&
    common < textWords.length &&
    normalizeWord(nameWords[common]) === normalizeWord(textWords[common])
  ) {
    common += 1;
  }

  return common >= 3 ? textWords.slice(common).join(' ') : text;
}

export function buildProductMetaDescription(
  candidates: any[],
  { name, brand, lang }: { name?: string; brand?: string; lang?: string } = {},
): string {
  const productName = normalizeMetaText(name);

  for (const candidate of candidates) {
    const normalized = normalizeMetaText(candidate);

    if (!normalized) {
      continue;
    }

    if (!productName) {
      return truncateMetaText(normalized);
    }

    const alreadyNamed =
      normalized
        .slice(0, productName.length + 20)
        .toLowerCase()
        .indexOf(productName.toLowerCase()) !== -1;

    if (alreadyNamed) {
      return truncateMetaText(normalized);
    }

    const rest = stripLeadingProductName(normalized, productName);

    if (
      rest !== normalized &&
      rest.split(' ').filter(Boolean).length < PRODUCT_DESCRIPTION_MIN_WORDS
    ) {
      break;
    }

    return truncateMetaText(`${productName} – ${rest}`);
  }

  return buildMetaDescription([], { name, brand, lang });
}

export function buildSectionMetaDescription(
  name?: string,
  lang?: string,
): string {
  const normalized = normalizeMetaText(name);

  if (!normalized) {
    return '';
  }

  return truncateMetaText(
    `${normalized} – ${resolveArticleSectionSuffixByLang(lang)}`,
  );
}

export function getLangAbsoluteUrl(url: string, lang?: string): string {
  const baseUrl = (resolveBaseUrlByLang(lang) || '').replace(/\/+$/, '');
  const path = (url || '').replace(/^\/+/, '');

  return path ? `${baseUrl}/${path}` : `${baseUrl}/`;
}

export function getProductLangUrl(product: any, lang?: string): string {
  const publishUrl = prop(product, 'publish.0.product_url', '') || '';
  const mainUrl = prop(product, 'url', '') || '';

  return lang === DEFAULT_LANG ? mainUrl || publishUrl : publishUrl || mainUrl;
}

export function getCategoryLangUrl(category: any): string {
  return prop(category, 'publish.0.url') || prop(category, 'url', '') || '';
}

export const HREFLANG_LANGS = ['sk', 'cz'];

export function normalizeUrlTranslationKey(pathname?: string): string {
  const normalized = (pathname || '/').split('?')[0].replace(/\/+$/, '');

  return normalized || '/';
}

export const X_DEFAULT_LANG = DEFAULT_LANG;

function withXDefault(
  alternates: Array<{ hreflang: string; href: string }>,
): Array<{ hreflang: string; href: string }> {
  if (alternates.length === 0) {
    return alternates;
  }

  const xDefaultHreflang = resolveHtmlLangByLang(X_DEFAULT_LANG);
  const xDefault =
    alternates.find(alternate => alternate.hreflang === xDefaultHreflang) ||
    alternates[0];

  return [...alternates, { hreflang: 'x-default', href: xDefault.href }];
}

export function buildHreflangAlternates(
  pathname: string,
  urlTranslations?: { [key: string]: Array<{ lang_id: string; url: string }> },
  lang?: string,
): Array<{ hreflang: string; href: string }> {
  const key = normalizeUrlTranslationKey(pathname);

  if (key === '/') {
    return withXDefault(
      HREFLANG_LANGS.map(hreflangLang => ({
        hreflang: resolveHtmlLangByLang(hreflangLang),
        href: getLangAbsoluteUrl('', hreflangLang),
      })),
    );
  }

  const translations = (urlTranslations || {})[key] || [];

  const alternates = translations
    .filter(
      translation =>
        translation &&
        translation.url &&
        HREFLANG_LANGS.indexOf(translation.lang_id) !== -1,
    )
    .map(translation => ({
      hreflang: resolveHtmlLangByLang(translation.lang_id),
      href: getLangAbsoluteUrl(translation.url, translation.lang_id),
    }));

  const currentHreflang = resolveHtmlLangByLang(lang);
  const hasSelfReference = alternates.some(
    alternate => alternate.hreflang === currentHreflang,
  );

  if (!hasSelfReference && HREFLANG_LANGS.indexOf(lang || '') !== -1) {
    alternates.unshift({
      hreflang: currentHreflang,
      href: getLangAbsoluteUrl(key, lang),
    });
  }

  return withXDefault(alternates);
}

export function getCmsMetaTags(content: any, image: string, lang?: string) {
  let metaTags: any = {};

  if (content && content.meta) {
    metaTags = content.meta;
  }
  if (!metaTags || !metaTags.title || !metaTags.title.length) {
    metaTags.title = content && content.name;
  }
  const description = buildMetaDescription(
    [
      prop(content, 'meta.description'),
      prop(content, 'anotation'),
      prop(content, 'annotation'),
      prop(content, 'short_description'),
      prop(content, 'body'),
    ],
    { name: metaTags.title, lang },
  );

  if (description) {
    metaTags.description = description;
    metaTags.og_description =
      buildMetaDescription([prop(content, 'meta.og_description')]) ||
      description;
  }

  metaTags.og_image = image || null;

  return metaTags;
}

export function getEbookMetaTags(ebook: any) {
  const content = prop(ebook, 'json_content');
  let metaTags: any = {};
  if (content && content.meta) {
    metaTags = content.meta;
  }

  if (!metaTags || !metaTags.title || !metaTags.title.length) {
    metaTags.title = content && content.name;
  }

  return metaTags;
}

export function getAuthorMetaTags(author: any) {
  const content = prop(author, 'json_content');
  let metaTags: any = {};
  if (content && content.meta) {
    metaTags = content.meta;
  }

  if (!metaTags || !metaTags.title || !metaTags.title.length) {
    metaTags.title = content && content.name;
  }

  return metaTags;
}

export function getProductMetaTags(
  product,
  defaultTitle?: string,
  lang?: string,
) {
  const productPublish = prop(product, 'publish', [])[0];
  const productName = resolveProductName(product);

  const metaTags: any = {};
  if (productPublish && prop(productPublish, 'content.id', null) !== null) {
    metaTags.title = defaultTitle
      ? `${productName} - ${defaultTitle}`
      : productName;
  } else {
    metaTags.title = defaultTitle;
  }

  metaTags.description =
    buildMetaDescription([
      prop(productPublish, 'content.json_content.meta.description'),
    ]) ||
    buildProductMetaDescription(
      [
        prop(productPublish, 'product_description'),
        prop(product, 'short_description'),
      ],
      { name: productName, brand: resolveProductBrand(product), lang },
    );

  metaTags.og_description = metaTags.description;

  metaTags.keywords = normalizeMetaText(
    prop(productPublish, 'content.json_content.meta.keywords'),
  );

  if (prop(product, 'picture')) {
    metaTags.og_image = encodeURI(
      `${process.env.REACT_APP_API_BASE_URL}/image?query=${product.picture}`,
    );
    metaTags.og_image_width = metaTags.og_image_height = 1005;
  }

  metaTags.og_title = metaTags.title;
  metaTags.og_type = 'product';

  const productLangUrl = getProductLangUrl(product, lang);
  if (productLangUrl) {
    metaTags.og_url = getLangAbsoluteUrl(productLangUrl, lang);
  }

  return metaTags;
}

export function getCategoryMetaTags(
  data,
  defaultTitle?: string,
  lang?: string,
) {
  if (!data) {
    return {};
  }
  const rawContent =
    prop(data, 'publish.0.content', null) || prop(data, 'content', null);

  const contentItem = Array.isArray(rawContent) ? rawContent[0] : rawContent;

  const meta = prop(contentItem || null, 'json_content.meta', null);

  const categoryName =
    prop(data, 'publish.0.category_name') ||
    prop(meta, 'title', null) ||
    `${data.category_name}`;

  const description = buildMetaDescription(
    [
      prop(meta, 'description', null),
      prop(contentItem || null, 'json_content.short_description'),
    ],
    { name: categoryName, lang },
  );

  const categoryLangUrl = getCategoryLangUrl(data);

  return {
    title: categoryName,
    description,
    keywords: normalizeMetaText(prop(meta, 'keywords', null)),
    og_article_author: prop(meta, 'og_article_author', null),
    og_article_section: prop(meta, 'og_article_section', null),
    og_article_tag: prop(meta, 'og_article_tag', null),
    og_description:
      buildMetaDescription([prop(meta, 'og_description', null)]) || description,
    og_image:
      prop(meta, 'og_image', null) || prop(data, 'image')
        ? getImagePath(prop(data, 'image'))
        : '',
    og_title:
      prop(data, 'publish.0.category_name') ||
      prop(meta, 'og_title', null) ||
      categoryName,
    og_type: prop(meta, 'og_type', 'website'),
    og_url:
      prop(meta, 'og_url', null) ||
      (categoryLangUrl ? getLangAbsoluteUrl(categoryLangUrl, lang) : null),
    og_image_width: 1005,
    og_image_height: 1005,
  };
}

export function getFooterArticleMetaTags(article) {
  // const content = prop(category, 'content', {});
  const metaTags: any = {};
  // if (content && content.id !== null) {
  //   const tags = prop(content.json_content, 'meta');
  //   if (tags && !isEmptyObj(tags)) {
  //     metaTags = tags;
  //   }
  // }

  if (article && (!metaTags.title || isEmptyObj(metaTags.title))) {
    metaTags.title = article.name;
  }

  return metaTags;
}
